import React, { useState } from "react";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Checkbox } from "components/ui/checkbox";
import { Shield, FileText, ExternalLink } from "lucide-react";

interface LegalStepProps {
  onNext: () => void;
}

export default function LegalStep({ onNext }: LegalStepProps) {
  const [acceptedBAA, setAcceptedBAA] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const handleContinue = () => {
    if (acceptedBAA && acceptedTerms) {
      onNext();
    } else {
      setShowErrors(true);
    }
  };

  const openBAA = () => {
    window.open(
      "https://drive.google.com/file/d/1vWZL-OnfKdOeQ64rBkkFHMTF7YaWzAe2/view",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openTerms = () => {
    window.open(
      "https://docs.google.com/document/d/1sOPgtPktx4YWc-p5YXWIztGEWusDG4wsP_txWhjpwNw/edit?tab=t.0#heading=h.uk55r5shz4n",
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-blue-50 rounded-xl">
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Legal Documents
        </h2>
        <p className="text-lg text-gray-600">
          Please review and accept our legal documents to continue with your
          setup
        </p>
      </div>

      <div className="space-y-6 mb-8">
        {/* Business Associate Agreement */}
        <Card className="border border-gray-200">
          <CardHeader className="pb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <CardTitle className="text-xl font-semibold">
                Business Associate Agreement (BAA)
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600">
              Our Business Associate Agreement ensures HIPAA compliance and
              protects patient health information in accordance with healthcare
              regulations.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={openBAA}
              className="w-full sm:w-auto border-[#005151] text-[#005151] hover:bg-[#005151] hover:text-white"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Review Business Associate Agreement
            </Button>
            <div className="space-y-2">
              <div className="flex items-start space-x-4">
                <Checkbox
                  id="baa"
                  checked={acceptedBAA}
                  onCheckedChange={(checked) => {
                    setAcceptedBAA(checked as boolean);
                    if (checked && showErrors) {
                      setShowErrors(false);
                    }
                  }}
                  className="border-gray-500 mt-1 h-5 w-5 data-[state=checked]:bg-[#005151] data-[state=checked]:border-[#005151] data-[state=checked]:text-white"
                />
                <label
                  htmlFor="baa"
                  className="text-base font-medium text-gray-700 leading-relaxed cursor-pointer flex-1"
                >
                  I have read and accept the Business Associate Agreement
                </label>
              </div>
              {showErrors && !acceptedBAA && (
                <p className="text-sm text-red-600 font-medium ml-9">
                  Please accept the Business Associate Agreement to continue
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Terms of Service */}
        <Card className="border border-gray-200">
          <CardHeader className="pb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <CardTitle className="text-xl font-semibold">
                Terms of Service
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600">
              Our Terms of Service outline the rights, responsibilities, and
              expectations for using the Health Harbor platform.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={openTerms}
              className="w-full sm:w-auto border-[#005151] text-[#005151] hover:bg-[#005151] hover:text-white"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Review Terms of Service
            </Button>
            <div className="space-y-2">
              <div className="flex items-start space-x-4">
                <Checkbox
                  id="terms"
                  checked={acceptedTerms}
                  onCheckedChange={(checked) => {
                    setAcceptedTerms(checked as boolean);
                    if (checked && showErrors) {
                      setShowErrors(false);
                    }
                  }}
                  className="border-gray-500 mt-1 h-5 w-5 data-[state=checked]:bg-[#005151] data-[state=checked]:border-[#005151] data-[state=checked]:text-white"
                />
                <label
                  htmlFor="terms"
                  className="text-base font-medium text-gray-700 leading-relaxed cursor-pointer flex-1"
                >
                  I have read and accept the Terms of Service
                </label>
              </div>
              {showErrors && !acceptedTerms && (
                <p className="text-sm text-red-600 font-medium ml-9">
                  Please accept the Terms of Service to continue
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleContinue}
          className="bg-[#005151] hover:bg-[#004040] text-white px-8"
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
